
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { addToCartExceedLimit, addToCartExceedMealsLimit, logOutFromFacebook } from '@/utils/helpers';
import ItemInformation from '@/components/menu/viewer/ItemInformation.vue';
import AddOns from '@/components/menu/viewer/AddOns.vue';
import Modal from '@/components/shared/Modal.vue';
import QuantitySelector from '@/components/menu/viewer/QuantitySelector.vue';
import WarningModal from '@/components/shared/WarningModal.vue';

const clickaway = require('vue-clickaway').mixin;
const namespace: string = 'cart';

type ItemState = 'sold_out' | 'unavailable' | 'dine_in_only' | 'ordering_unavailable' | 'add_to_order';

@Component<MenuItemViewer>({
	components: {
		AddOns,
		ItemInformation,
		Modal,
		QuantitySelector,
		WarningModal
	},
	mixins: [ clickaway ]
})
export default class MenuItemViewer extends Vue {
	@Prop({ type: Array, required: true, default: () => [] }) private menuItems!: MenuItem[];
	@Prop({ type: Object, required: false }) private orderAgainItem!: MenuItem|null;
	@Prop({ type: Number, required: false, default: 0 }) private initialItemId!: number|null;
	@Prop({ type: Number, required: false, default: undefined }) private initialSectionId!: number|null|undefined;
	@Prop({ type: Boolean, required: true, default: false }) private isViewOnly!: boolean;
	@Prop({ type: Boolean, required: true, default: false }) private orderPaused!: boolean;
	@Prop({ type: String, required: false, default: '' }) private orderDate!: string;
	@Prop({ type: Boolean, required: false, default: undefined }) private acceptsTakeout!: boolean;
	@Prop({ type: Boolean, required: false, default: true }) private showArrows!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private isFeaturedItem!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) private isUpsellItem!: boolean;
	@Action('setTemporaryItem', { namespace }) private setTemporaryItem!: (temporaryItemPayload: { tempItem: MenuItem|null, quantity?: number }) => void;
	@Action('addToCart', { namespace }) private addToCart!: (extraData: any) => void;
	@Action('addToRotationalMenuCart', { namespace }) private addToRotationalMenuCart!: (extraData: any) => void;
	@Action('updateTemporaryItemQuantity', { namespace }) private updateTemporaryItemQuantity!: (quantity?: number) => void;
	@Action('removeTemporaryItem', { namespace }) private removeTemporaryItem!: () => void;
	@Action('setLegal', { namespace }) private setLegal!: (value: boolean) => void;
	@Action('logout', { namespace: 'auth' }) private logout!: () => Promise<void>;
	@Getter('getOrderPrice', { namespace }) private orderPrice!: string;
	@Getter('getLegalCheck', { namespace }) private legalChecked!: boolean;
	@Getter('getTemporaryOrderOptions', { namespace }) private temporaryOrderOptions!: OrderOptionGroup[] | null;
	@Getter('getTemporaryQuantity', { namespace }) private quantity!: number;
	@Getter('isTakeOut', { namespace }) private isTakeOut!: boolean;
	@Getter('isGenericCatering', { namespace }) private isGenericCatering!: boolean;
	@Getter('isAnonymousUser', { namespace: 'auth' }) private isAnonymousUser!: boolean;
	@Getter('getUser', { namespace: 'auth' }) private user!: UserInfo;
	@Getter('isSocialLoginUser', { namespace: 'auth' }) private isSocialLoginUser!: boolean;
	@Getter('getSearchInput', { namespace: 'search' }) private searchInput!: string;
	@Getter('getMaxItemsPerCart', { namespace: 'restaurant' }) private maxItemsPerCart!: number;
	@Getter('getMaxMealsPerCart', { namespace: 'restaurant' }) private maxMealsPerCart!: number;
	@Getter('isK12Location', { namespace: 'restaurant' }) private isK12Location!: boolean;
	@Getter('orderingRequiresLogin', { namespace: 'restaurant' }) private orderingRequiresLogin!: boolean;
	@Getter('hidePricing', { namespace: 'restaurant' }) private hidePricing!: boolean;
	@Watch('menuItem')
	onItemsChanged() {
		this.checkIfImageExists();
	}
	// Todo: refactor once we refactor the addons/subaddons completely (could easily apply recursivity here)
	@Watch('temporaryOrderOptions', { deep: true })
	onTemporaryOrderOptionsChanged() {
		// Reset the calorie count beforehand
		this.calories = this.menuItem?.calories ? this.menuItem.calories : 0;
		this.addonsCalories = 0;

		if(this.temporaryOrderOptions && this.temporaryOrderOptions.length) {

			// Addons
			this.temporaryOrderOptions.forEach((temporaryOrderOption: OrderOptionGroup) => {

				if(temporaryOrderOption.values && temporaryOrderOption.values.length) {
					temporaryOrderOption.values.forEach((value: OrderOption) => {
						const optionQuantity = value.quantity ? value.quantity : 1;

						if(temporaryOrderOption.optionGroupType === 'pricing') {
							this.calories = value.calories ? value.calories : this.menuItem?.calories;
						}
						else {
							this.addonsCalories += value.calories ? (value.calories * optionQuantity) : 0;
						}

						// Sub addons
						if(value.options && value.options.length) {
							value.options.forEach((subTemporaryOrderOption: OrderOptionGroup) => {

								if(subTemporaryOrderOption.values && subTemporaryOrderOption.values.length) {
									subTemporaryOrderOption.values.forEach((subValue: OrderOption) => {
										this.addonsCalories += subValue.calories ? (subValue.calories * optionQuantity) : 0;
									});
								}
							});
						}
					});
				}
			});
		}

		// If the addons calories are at 0, and
		this.calories = Number.isInteger(this.menuItem?.calories) || this.addonsCalories ? this.calories + this.addonsCalories : null;

		const interval = setInterval(() => {
			if(Number.isInteger(this.calories)) {
				if(this.displayCalories !== this.calories) {
					let change = (this.calories! - this.displayCalories) / 10;
					change = change >= 0 ? Math.ceil(change) : Math.floor(change);
					this.displayCalories = this.displayCalories! + change;
				}
				else {
					clearInterval(interval);
				}
			}
			else {
				this.displayCalories = 0;
				clearInterval(interval);
			}
		}, 50);
	}

	// Error modal properties
	private errorModalOpened: boolean = false;
	private isK12SocialLoginError: boolean = false;
	private requiredOptionsFilled: boolean = false;
	private showErrorMessages: boolean = false;
	private processing: boolean = false;
	private showImage: boolean = true;
	private legalCheckRequired: boolean = false;
	private specialInstructions: string = '';
	private calories: number|null|undefined = this.menuItem?.calories;
	private displayCalories: number = this.calories ? this.calories : 0;
	private addonsCalories: number = 0;
	private xDown: number | null = null;
	private yDown: number | null = null;
	private currentItemId: number|null = null;
	private currentSectionId: number|null|undefined = null;
	private currentItemState: ItemState | '' = '';
	private warningModalProps: WarningModalProps = {
		show: false,
		title: '',
		message: '',
		confirmButtonText: this.$t('menu.error_validation_dialog_buttons')[0],
		cancelButtonText: '',
		type: 'warning',
		hideCloseButton: false,
		showCheckbox: false,
		checkboxLabel: '',
		onClose: () => this.resetWarningModal(),
		onCancel: () => this.resetWarningModal(),
		onConfirm: () => this.closeWarningModal()
	};

	// Computed instead of data so the labels re-translate with the locale resolved after restaurant is loaded
	private get ITEM_STATES(): Record<ItemState, string> {
		return {
			sold_out: this.$t('menu.item_viewer.btn_add_to_cart_sold_out'),
			unavailable: this.$t('menu.item_viewer.btn_add_to_cart_unavailable'),
			dine_in_only: this.$t('menu.item_viewer.btn_add_to_cart_dine_in_only'),
			ordering_unavailable: this.$t('menu.item_viewer.btn_add_to_cart_ordering_unavailable'),
			add_to_order: this.$t('menu.item_viewer.btn_add_to_cart')
		};
	}

	private get imageURL(): string {
		if (process.env.NODE_ENV === 'development') {
			return `${this.menuItem!.image}`;
		}
		return `${process.env.VUE_APP_IMAGE_BUCKET}/${this.menuItem!.image}`;
	}

	/**
	 * Get the menu item
	 *
	 * @return {MenuItem | null}
	 */
	private get menuItem(): MenuItem|null {
		if (this.orderAgainItem) {
			return this.orderAgainItem;
		}
		if (this.menuItems) {
			const menuItem: MenuItem|undefined = this.currentItemId ? this.menuItems.find((i: MenuItem) => i.id === this.currentItemId && i.section_id === this.currentSectionId) : this.menuItems.find((i: MenuItem) => i.id === this.initialItemId && i.section_id === this.initialSectionId);
			if (menuItem) {
				return menuItem;
			}
		}
		return null;
	}

	/**
	 * Set the temporary item and verify its image on creation
	 *
	 * @return {void}
	 */
	private created(): void {
		this.setTemporaryItem({ tempItem: this.menuItem, quantity: this.menuItem?.min_quantity });
		this.checkIfImageExists();
	}

	/**
	 * Set the temp item to whatever item is being viewed.
	 * Add touch listeners for swipe events.
	 *
	 * @return {void}
	 */
	private mounted() {
		this.setItemState(this.menuItem!);
		document.addEventListener('touchstart', this.handleTouchStart, false);
		document.addEventListener('touchmove', this.handleTouchMove, false);
	}

	/**
	 * Remove touch listeners
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		document.removeEventListener('touchstart', this.handleTouchStart);
		document.removeEventListener('touchmove', this.handleTouchMove);
	}

	/**
	 * Check if the image exists or is broken, if it's broken
	 * we remove the image from the DOM.
	 *
	 * @return {void}
	 */
	private checkIfImageExists() {
		if(this.menuItem && this.menuItem.image) {
			let img = new Image();
			let vm = this;
			img.onerror = () => {
				vm.showImage = false;
			};
			img.onload = () => {
				vm.showImage = true;
			};
			img.src = this.imageURL;
		}
	}

	/**
	 * Update the item state that will be used to determine
	 * the button state if ordering is available or not.
	 * Goes from biggest priority to lowest.
	 *
	 * @param {MenuItem}
	 * @return {void}
	 */
	private setItemState(menuItem: MenuItem): void {
		if(menuItem.sold_out) {
			this.currentItemState = 'sold_out';
		}
		else if(menuItem.unavailable) {
			this.currentItemState = 'unavailable';
		}
		else if(this.isTakeOut && this.acceptsTakeout === false) {
			this.currentItemState = 'dine_in_only';
		}
		else if(this.orderPaused) {
			this.currentItemState = 'ordering_unavailable';
		}
		else {
			this.currentItemState = 'add_to_order';
		}
	}

	/**
	 * Set the temp item to whatever item is being viewed.
	 * Add touch listeners for swipe events.
	 *
	 * @return {void}
	 */
	private updateSpecialInstructions(specialInstructions: string) {
		this.specialInstructions = specialInstructions;
	}

	/**
	 * Checks the first touch of the user for swipe listen
	 *
	 * @return {void}
	 */
	private handleTouchStart(e: TouchEvent): void {
		const firstTouch = e.touches[0];
		this.xDown = firstTouch.clientX;
		this.yDown = firstTouch.clientY;
	}

	/**
	 * Listen to swipe event to switch to prev/next item
	 *
	 * @param {TouchEvent} e
	 * @return {void}
	 */
	private handleTouchMove(e: TouchEvent): void {
		if(!this.searchInput && this.showArrows) {
			if (!this.xDown || !this.yDown) {
				return;
			}

			const xUp = e.touches[0].clientX;
			const yUp = e.touches[0].clientY;
			const xDiff = this.xDown - xUp;
			const yDiff = this.yDown - yUp;

			if (Math.abs(xDiff) > Math.abs(yDiff)) {
				if (xDiff > 10) {
					/* left swipe */
					this.prevMenuItem();
				}
				else if (xDiff < -10) {
					/* right swipe */
					this.nextMenuItem();
				}
			}
			this.xDown = null;
			this.yDown = null;
		}
	}

	/**
	 * Go to next menu item
	 *
	 * @return {void}
	 */
	private nextMenuItem(): void {
		const currentIndex = this.currentItemId ? this.menuItems.findIndex((p:MenuItem) => p.id === this.currentItemId && p.section_id === this.currentSectionId) : this.menuItems.findIndex((p:MenuItem) => p.id === this.initialItemId && p.section_id === this.initialSectionId);
		const nextIndex = currentIndex + 1;
		if (nextIndex < this.menuItems.length) {
			this.currentItemId = this.menuItems[nextIndex].id;
			this.currentSectionId = this.menuItems[nextIndex].section_id;
		}
		else {
			this.currentItemId = this.menuItems[0].id;
			this.currentSectionId = this.menuItems[0].section_id;
		}
		this.setTemporaryItem({ tempItem: this.menuItem, quantity: this.menuItem?.min_quantity });
	}

	/**
	 * Go to previous menu item
	 *
	 * @return {void}
	 */
	private prevMenuItem(): void {
		const currentIndex = this.currentItemId ? this.menuItems.findIndex((i:MenuItem) => i.id === this.currentItemId && i.section_id === this.currentSectionId) : this.menuItems.findIndex((i:MenuItem) => i.id === this.initialItemId && i.section_id === this.initialSectionId);
		const prevIndex = currentIndex - 1;
		if (prevIndex > -1) {
			this.currentItemId = this.menuItems[prevIndex].id;
			this.currentSectionId = this.menuItems[prevIndex].section_id;
		}
		else {
			this.currentItemId = this.menuItems[this.menuItems.length - 1].id;
			this.currentSectionId = this.menuItems[this.menuItems.length - 1].section_id;
		}
		this.setTemporaryItem({ tempItem: this.menuItem, quantity: this.menuItem?.min_quantity });
	}

	/**
	 * Add the temp item to the cart. Checks if the required options are filled before validating and adding to the array of items.
	 * * If we update the logic of adding the item to the cart, check if it impacts addItemToCart in CartUpsellItemList.vue
	 *
	 * @return {Promise}
	 */
	private async addItemToCart(): Promise<void> {
		this.processing = true;

		// Don't allow adding items to the cart if the user is anonymous and the restaurant is generic catering
		if(this.checkIfLoginIsRequired()) {
			return void (this.processing = false);
		}

		if(this.requiredOptionsFilled) {

			// If the item is alcoholic and the legal check has not been checked yet
			// we don't allow adding that item to the cart until it is checked.
			if(this.legalCheck()) {
				let extraData = {
					special_instructions: this.specialInstructions,
					originalPrice: this.menuItem!.price,
					type: this.getExtraDataType(),
					orderDate: this.orderDate
				};
				if (addToCartExceedLimit()) {
					this.warningModalProps.show = true;
					this.warningModalProps.message = this.$tc('menu.item_viewer.error_item_limit_exceeded', this.maxItemsPerCart, { maxItemsPerCart: this.maxItemsPerCart });
				}
				else if (addToCartExceedMealsLimit(this.orderDate)) {
					this.warningModalProps.show = true;
					this.warningModalProps.message = this.$tc('menu.item_viewer.error_quantity_meals_limit_exceeded', this.maxMealsPerCart, { maxMealsPerCart: this.maxMealsPerCart });
				}
				else if (this.isK12Location && this.isSocialLoginUser) {
					// K12 social login restriction
					this.isK12SocialLoginError = true;
					this.warningModalProps.show = true;
					this.warningModalProps.message = this.$t('utils.vuex_store.cart.k12_social_login_restriction');
				}
				else {
					try {
						await this.addToCart(extraData);
						this.$emit('item-added');
					}
					catch (error) {
						this.warningModalProps.show = true;
						this.warningModalProps.type = 'error';
						this.warningModalProps.message = (error as any).response?.data?.message || this.$t('menu.item_viewer.error_adding_item_to_cart');
					}
					finally {
						if(!this.warningModalProps.show) {
							this.closeModal();
						}
					}
				}
			}
		}
		else {
			this.showErrorMessages = true;
			alert(this.$t('menu.item_viewer.error_required_fields_unfilled'));
			setTimeout(() => {
				const requiredLabel = document.getElementsByClassName('red')[0] as HTMLElement;
				const requiredLabelStyle = window.getComputedStyle(requiredLabel);
				document.getElementsByClassName('menu-content')[0].scrollTo({ top: requiredLabel.offsetTop -
					requiredLabel.clientHeight - parseFloat(requiredLabelStyle['marginTop']!) -
					parseFloat(requiredLabelStyle['marginBottom']!) - 26, behavior: 'smooth' });
			}, 150);
		}
		this.processing = false;
	}

	/**
	 * Select the type property in the extraData object when adding an item to the cart.
	 *
	 * @return {string | undefined}
	 */
	private getExtraDataType(): string | undefined {
		if (this.menuItem?.lastOrderedOptions) {
			return 'reorder';
		}
		if (this.isFeaturedItem) {
			return 'addFeaturedItem';
		}
		if (this.isUpsellItem) {
			return 'addUpsellItem';
		}
		return undefined;
	}

	/**
	 * Display an error when adding an item to the cart, if ordering requires login and the user is anonymous
	 *
	 * @return {boolean}
	 */
	private checkIfLoginIsRequired(): boolean {
		if(this.isAnonymousUser && this.orderingRequiresLogin) {
			this.warningModalProps.show = true;
			this.warningModalProps.message = this.$t('menu.item_viewer.login_required_warning');
			this.warningModalProps.confirmButtonText = this.$t('menu.item_viewer.generic_catering_warning_button');
			this.warningModalProps.cancelButtonText = this.$t('utils.close');
			this.warningModalProps.onConfirm = () => {
				this.redirectToLogin();
			};
			this.processing = false;
			return true;
		}
		return false;
	}

	/**
	 * Redirect user to log in
	 *
	 * @return {void}
	 */
	private redirectToLogin(): void {
		this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}

	/**
	 * Set the required options filled boolean to whatever value
	 * is returned from the child's event after an option was
	 * selected/unselected
	 *
	 * @param {boolean} value
	 * @return {void}
	 */
	private setRequiredOptionsFilledBool(value: boolean): void {
		this.requiredOptionsFilled = value;
	}

	/**
	 * Check if the item contains alcohol to trigger the legal check
	 *
	 * @return {void}
	 */
	private legalCheck(): boolean {
		if(this.menuItem && this.menuItem.allergens) {
			if(this.menuItem.allergens.includes('Alcohol')) {
				if(this.legalChecked) {
					return true;
				}
				else {
					this.legalCheckRequired = true;
					this.warningModalProps.show = true;
					this.warningModalProps.message = this.$t('menu.item_viewer.alcohol_legal_warning');
					this.warningModalProps.hideCloseButton = true;
					this.warningModalProps.showCheckbox = true;
					this.warningModalProps.checkboxLabel = this.$t('utils.error_validation_dialog.legal_age_checkbox');
					return false;
				}
			}
		}
		return true;
	}

	/**
	 * Close the error modal with a timeout to avoid edge and IE problems
	 * that would close both modals because of clickaway
	 *
	 * @return {void}
	 */
	private async closeWarningModal(): Promise<void> {
		setTimeout(async () => {
			if (this.legalCheckRequired && this.legalChecked) {
				this.addItemToCart();
			}

			// If this is a K12 social login error, logout and redirect to login
			if (this.isK12SocialLoginError) {
				try {
					this.logoutUser();

					await this.logout();
					this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
				}
				catch (error) {
					this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
				}
				this.isK12SocialLoginError = false;
			}

			this.resetWarningModal();
			this.legalCheckRequired = false;
		}, 150);
	}

	/**
	 * Reset the warning modal properties to default values
	 *
	 * @return {void}
	 */
	private resetWarningModal(): void {
		this.warningModalProps = {
			show: false,
			title: '',
			message: '',
			confirmButtonText: this.$t('menu.error_validation_dialog_buttons')[0],
			cancelButtonText: '',
			type: 'warning',
			hideCloseButton: false,
			showCheckbox: false,
			checkboxLabel: '',
			onClose: () => this.warningModalProps.show = false,
			onCancel: () => this.warningModalProps.show = false,
			onConfirm: () => this.closeWarningModal()
		};
	}

	/**
	 * Send close event to close the menu viewer
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}

	/**
	 * Logout from the specific social provider that was used for login
	 *
	 * @return {void}
	 */
	private async logoutUser(): Promise<void> {
		logOutFromFacebook();
		await this.logout();
	}
}
